import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Bed, Bath, Maximize2, Trees, Car, Waves } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { fetchProperty, postContact } from "../lib/api";

function formatPrice(price, currency = "EUR") {
  const n = Number(price);
  if (!n) return "Price on request";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(n);
}

function buildFallback(reference) {
  return {
    reference,
    location: "Marbella",
    area: "Costa del Sol",
    country: "Spain",
    type: "Luxury Villa",
    bedrooms: 5,
    bathrooms: 5,
    built: 620,
    garden_plot: 1200,
    price: 6500000,
    currency: "EUR",
    pool: 1,
    garden: 1,
    parking: 1,
    main_image:
      "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1600&q=80",
    images: [
      "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1600&q=80",
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80",
      "https://images.unsplash.com/photo-1567496898669-ee935f5f647a?w=1600&q=80",
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80",
    ],
    description: {
      nl:
        "Een uitzonderlijke moderne villa in een van de meest gewilde urbanisaties van Marbella. " +
        "Hoogwaardige afwerking, ruime leefruimtes met panoramische ramen, infinity pool en " +
        "professioneel aangelegde tuinen. Inclusief private spa, wijnkelder en chef's kitchen.",
      en:
        "An exceptional contemporary villa in one of Marbella's most coveted urbanisations. " +
        "Premium finishes, expansive living spaces with panoramic windows, infinity pool, and " +
        "professionally designed gardens. Includes private spa, wine cellar and chef's kitchen.",
      de:
        "Eine außergewöhnliche moderne Villa in einer der begehrtesten Wohnanlagen Marbellas. " +
        "Premium-Ausstattung, weitläufige Wohnbereiche mit Panoramafenstern, Infinity-Pool und " +
        "professionell angelegte Gärten. Inklusive privatem Spa, Weinkeller und Chef's Kitchen.",
    },
    features: [
      { Type: "Setting", Value: ["Close To Golf", "Close To Sea"] },
      { Type: "Orientation", Value: ["South", "South West"] },
      { Type: "Pool", Value: ["Private", "Heated"] },
      { Type: "Climate Control", Value: ["Air Conditioning", "Underfloor Heating"] },
      { Type: "Views", Value: ["Sea", "Mountain", "Garden"] },
      { Type: "Features", Value: ["Lift", "Private Terrace", "Gym", "Sauna"] },
    ],
  };
}

export default function PropertyDetail() {
  const { reference } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitState, setSubmitState] = useState(""); // "", "sending", "ok", "error"

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchProperty(reference)
      .then((d) => {
        if (!mounted) return;
        if (d.ok && d.property) {
          setData(d.property);
        } else {
          // Fallback when Resales API is unreachable from dev IP
          setData(buildFallback(reference));
        }
      })
      .catch(() => {
        if (!mounted) return;
        setData(buildFallback(reference));
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [reference]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitState("sending");
    try {
      await postContact({
        ...form,
        source: `property:${reference}`,
        message: `${form.message}\n\n[Property ref: ${reference}]`,
      });
      setSubmitState("ok");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      setSubmitState("error");
    }
  };

  if (loading) {
    return (
      <div className="bg-charcoal pt-40 pb-24 min-h-screen" data-testid="property-loading">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          <div className="h-[60vh] bg-charcoal-surface animate-pulse" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-charcoal pt-40 pb-24 min-h-screen" data-testid="property-notfound">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 text-center">
          <p className="text-cream-muted">{t.properties.noResults}</p>
          <Link to="/aanbod" className="btn-gold-outline mt-8 inline-flex">
            <ArrowLeft size={14} strokeWidth={1.5} /> {t.detail.back}
          </Link>
        </div>
      </div>
    );
  }

  const images = data.images && data.images.length > 0 ? data.images : data.main_image ? [data.main_image] : [];
  const heroImg = images[activeImg] || data.main_image;
  const desc = data.description || {};
  const descText = desc[lang] || desc.en || desc.nl || desc.de || "";

  return (
    <div className="bg-charcoal pt-28 pb-24" data-testid="page-property-detail">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <Link to="/aanbod" className="inline-flex items-center gap-2 text-cream-muted hover:text-gold text-xs uppercase tracking-[0.22em] mb-8" data-testid="back-to-list">
          <ArrowLeft size={14} strokeWidth={1.5} /> {t.detail.back}
        </Link>

        {/* Hero image */}
        <div className="relative aspect-[16/9] bg-charcoal-surface overflow-hidden mb-4">
          {heroImg ? (
            <img src={heroImg} alt={data.type} className="w-full h-full object-cover" />
          ) : null}
          <div className="absolute top-4 left-4">
            <span className="overline bg-charcoal-surface/90 px-3 py-1.5">
              {t.detail.reference} {data.reference}
            </span>
          </div>
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-12" data-testid="property-thumbs">
            {images.slice(0, 8).map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`aspect-square overflow-hidden border ${
                  i === activeImg ? "border-gold" : "border-white/10"
                }`}
                data-testid={`thumb-${i}`}
              >
                <img src={src} alt={`thumb-${i}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Main body */}
        <div className="grid lg:grid-cols-3 gap-14">
          <div className="lg:col-span-2">
            <p className="overline">{data.location || data.area} · {data.country}</p>
            <h1 className="font-serif text-4xl md:text-6xl text-cream leading-tight mt-3 mb-4">
              {data.type}
            </h1>
            <p className="font-serif text-3xl md:text-4xl text-gold">
              {formatPrice(data.price, data.currency)}
            </p>

            {/* Specs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 border-t border-b border-white/10 py-8">
              {data.bedrooms != null && (
                <Spec icon={Bed} label={t.detail.bedrooms} value={data.bedrooms} />
              )}
              {data.bathrooms != null && (
                <Spec icon={Bath} label={t.detail.bathrooms} value={data.bathrooms} />
              )}
              {data.built ? (
                <Spec icon={Maximize2} label={t.detail.built} value={`${data.built} m²`} />
              ) : null}
              {data.garden_plot ? (
                <Spec icon={Trees} label={t.detail.plot} value={`${data.garden_plot} m²`} />
              ) : null}
            </div>

            {/* Features quick */}
            <div className="flex flex-wrap gap-3 mt-6">
              {data.pool ? <Tag icon={Waves}>{t.detail.pool}</Tag> : null}
              {data.garden ? <Tag icon={Trees}>{t.detail.garden}</Tag> : null}
              {data.parking ? <Tag icon={Car}>{t.detail.parking}</Tag> : null}
            </div>

            {/* Description */}
            {descText && (
              <div className="mt-14">
                <p className="overline mb-4">{t.detail.description}</p>
                <div className="text-cream-muted leading-relaxed whitespace-pre-wrap text-base font-light max-w-3xl">
                  {descText}
                </div>
              </div>
            )}

            {/* Features list */}
            {Array.isArray(data.features) && data.features.length > 0 && (
              <div className="mt-14">
                <p className="overline mb-4">{t.detail.features}</p>
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
                  {data.features.map((cat, i) => (
                    <div key={i}>
                      <p className="text-gold text-xs uppercase tracking-[0.2em] mb-1">{cat.Type}</p>
                      <p className="text-cream-muted text-sm">
                        {Array.isArray(cat.Value) ? cat.Value.join(", ") : String(cat.Value)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Inquiry card */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 border border-gold/30 p-7 bg-charcoal-surface">
              <p className="overline">{t.detail.contactAgent}</p>
              <h3 className="font-serif text-3xl text-cream leading-tight mt-3 mb-6">
                {t.detail.requestInfo}
              </h3>

              {submitState === "ok" ? (
                <p className="text-gold text-sm border border-gold/40 p-4" data-testid="inquiry-success">
                  {t.detail.inquirySent}
                </p>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="inquiry-form">
                  <input
                    required
                    placeholder={t.detail.name}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="input-luxury"
                    data-testid="inquiry-name"
                  />
                  <input
                    required
                    type="email"
                    placeholder={t.detail.email}
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="input-luxury"
                    data-testid="inquiry-email"
                  />
                  <input
                    placeholder={t.detail.phone}
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    className="input-luxury"
                    data-testid="inquiry-phone"
                  />
                  <textarea
                    required
                    rows={4}
                    placeholder={t.detail.message}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className="input-luxury resize-none"
                    data-testid="inquiry-message"
                  />
                  {submitState === "error" && (
                    <p className="text-red-400 text-xs">{t.detail.inquiryError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitState === "sending"}
                    className="btn-gold-solid w-full mt-2 disabled:opacity-60"
                    data-testid="inquiry-submit"
                  >
                    {t.detail.sendInquiry}
                  </button>
                </form>
              )}
              <Link to="/diensten#viewing-experience" className="btn-gold-outline w-full mt-3 text-center" data-testid="inquiry-book-viewing">
                {t.detail.bookViewing}
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={20} strokeWidth={1} className="text-gold mt-0.5" />
      <div>
        <p className="text-cream-subtle text-xs uppercase tracking-[0.18em]">{label}</p>
        <p className="text-cream text-lg font-serif">{value}</p>
      </div>
    </div>
  );
}

function Tag({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 text-xs text-cream-muted">
      <Icon size={14} strokeWidth={1.25} className="text-gold" />
      {children}
    </span>
  );
}
